import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, ArrowUpRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const INITIALS_COLORS = ["bg-navy-900", "bg-gold-500", "bg-navy-800", "bg-gold-600"];

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/team`).then(r => setTeam(r.data)).catch(() => setTeam([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="team-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">Our People</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">Leadership that inspires confidence.</h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">Our team combines decades of institutional banking experience with an entrepreneurial spirit, delivering world-class advisory to India's growth enterprises.</p>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="text-center py-20"><div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200">
              {team.map((m, i) => (
                <div key={m.id} data-testid={`team-member-${i}`} className={`p-10 md:p-12 ${i < team.length - 2 ? 'border-b border-gray-200' : ''} ${i % 2 === 0 ? 'md:border-r border-gray-200' : ''}`}>
                  {m.image_url ? (
                    <img src={m.image_url} alt={m.name} className="w-20 h-20 object-cover rounded-sm mb-6 border border-gray-200" />
                  ) : (
                    <div className={`w-16 h-16 ${INITIALS_COLORS[i % INITIALS_COLORS.length]} rounded-sm flex items-center justify-center mb-6`}>
                      <span className="text-white font-serif text-2xl font-light">{m.name?.split(" ").map(n => n[0]).join("")}</span>
                    </div>
                  )}
                  <h3 className="font-serif text-2xl text-navy-900 mb-1">{m.name}</h3>
                  <p className="text-gold-500 font-sans text-sm font-medium mb-4">{m.title}</p>
                  <p className="text-gray-500 font-sans text-sm leading-relaxed mb-4">{m.bio}</p>
                  {m.experience && m.experience.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {m.experience.map(exp => <span key={exp} className="text-xs font-sans text-gray-500 bg-gray-100 px-3 py-1">Ex-{exp}</span>)}
                    </div>
                  )}
                  {m.linkedin && m.linkedin !== "#" && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-navy-900 hover:text-gold-500 transition-colors"><Linkedin className="w-4 h-4" strokeWidth={1.5} /></a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="py-24 md:py-32 bg-navy-900 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight mb-6">Interested in joining our team?</h2>
          <p className="text-white/60 font-sans mb-10 max-w-xl mx-auto">We're always looking for exceptional talent who share our passion for financial excellence.</p>
          <Link to="/contact" data-testid="team-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">Get in Touch <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
